import { Check, ChevronLeft, ListFilter, Plus, Search, X } from 'lucide-react';
import { type ComponentType, type ReactNode, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { cn } from '@apps/web/lib/utils';

// A generic dimension-driven filter bar shared across admin pages (e.g. agent
// traces and calls). It is parameterized by the set of dimension keys `K` so
// each page keeps its own narrow key union while reusing the UI.

export type FilterOption = {
    readonly value: string;
    readonly label: string;
    readonly count?: number;
};

export type FilterOperator = 'is' | 'is_all' | 'is_not';

const DEFAULT_FILTER_OPERATOR: FilterOperator = 'is';

export type FilterDimension<K extends string = string> = {
    readonly key: K;
    readonly label: string;
    readonly operatorLabel?: 'is' | 'is not';
    readonly operators?: Array<FilterOperator>;
    readonly icon: ComponentType<{ readonly className?: string }>;
    readonly options: Array<FilterOption>;
    readonly multiSelect: boolean;
    readonly searchable?: boolean;
    readonly renderValuePicker?: (props: {
        readonly selectedValues: Array<string>;
        readonly onSelect: (value: string) => void;
    }) => ReactNode;
};

export type FilterChip<K extends string = string> = {
    readonly dimension: K;
    readonly operator?: FilterOperator;
    readonly values: Array<string>;
};

function chipOperator<K extends string>(chip: FilterChip<K>): FilterOperator {
    return chip.operator ?? DEFAULT_FILTER_OPERATOR;
}

function dimensionOperators<K extends string>(
    dimension: FilterDimension<K>,
): Array<FilterOperator> {
    return dimension.operators ?? [DEFAULT_FILTER_OPERATOR];
}

function operatorLabel(operator: FilterOperator, valueCount: number): string {
    if (operator === 'is_not') {
        return 'is not';
    }
    if (operator === 'is_all') {
        return 'is all of';
    }
    return valueCount > 1 ? 'is either of' : 'is';
}

function isSameChip<K extends string>(
    chip: FilterChip<K>,
    dimension: FilterDimension<K>,
    operator: FilterOperator,
): boolean {
    return chip.dimension === dimension.key && chipOperator(chip) === operator;
}

function mergeChipValues(
    left: Array<string>,
    right: Array<string>,
): Array<string> {
    return [...new Set([...left, ...right])];
}

function searchMatchRank(value: string, query: string): number | null {
    const normalizedValue = value.toLowerCase();
    const matchesWord = normalizedValue
        .split(/[\s_-]+/)
        .some((word) => word.startsWith(query));
    if (normalizedValue.startsWith(query) || matchesWord) {
        return 0;
    }
    return normalizedValue.includes(query) ? 1 : null;
}

/**
 * Toggles a value within the chip for the given dimension. Single-select
 * dimensions replace their value; multi-select dimensions toggle membership
 * and drop the chip when the last value is removed.
 */
export function toggleChipValue<K extends string>(
    chips: Array<FilterChip<K>>,
    dimension: FilterDimension<K>,
    value: string,
    operator: FilterOperator = DEFAULT_FILTER_OPERATOR,
): Array<FilterChip<K>> {
    const existing = chips.find((chip) =>
        isSameChip(chip, dimension, operator),
    );
    if (!existing) {
        return [
            ...chips,
            { dimension: dimension.key, operator, values: [value] },
        ];
    }
    if (!dimension.multiSelect) {
        return chips.map((chip) =>
            chip === existing ? { ...chip, values: [value] } : chip,
        );
    }
    const nextValues = existing.values.includes(value)
        ? existing.values.filter((existingValue) => existingValue !== value)
        : [...existing.values, value];
    if (nextValues.length === 0) {
        return chips.filter((chip) => chip !== existing);
    }
    return chips.map((chip) =>
        chip === existing ? { ...chip, values: nextValues } : chip,
    );
}

export function setChipOperator<K extends string>(
    chips: Array<FilterChip<K>>,
    chip: FilterChip<K>,
    dimension: FilterDimension<K>,
    operator: FilterOperator,
): Array<FilterChip<K>> {
    const existingWithOperator = chips.find(
        (candidate) =>
            candidate !== chip && isSameChip(candidate, dimension, operator),
    );
    if (existingWithOperator) {
        return chips
            .filter((candidate) => candidate !== chip)
            .map((candidate) =>
                candidate === existingWithOperator
                    ? {
                          ...candidate,
                          values: mergeChipValues(
                              candidate.values,
                              chip.values,
                          ),
                      }
                    : candidate,
            );
    }
    return chips.map((candidate) =>
        candidate === chip ? { ...candidate, operator } : candidate,
    );
}

const COMPACT_COMMAND_CLASS =
    '[&_[cmdk-group]]:p-0 [&_[cmdk-input-wrapper]]:h-7 [&_[cmdk-input-wrapper]]:px-2 [&_[cmdk-input-wrapper]_svg]:size-3.5 [&_[cmdk-input]]:h-7 [&_[cmdk-input]]:py-0 [&_[cmdk-input]]:text-xs [&_[cmdk-item]]:gap-1.5 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-1 [&_[cmdk-item]]:text-xs';

function FilterMenuSearchInput({
    value,
    onChange,
}: {
    readonly value: string;
    readonly onChange: (value: string) => void;
}) {
    return (
        <div className="flex h-7 items-center gap-1.5 border-b px-2">
            <Search className="text-muted-foreground size-3.5 shrink-0 opacity-70" />
            <input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder="Add filter…"
                className="placeholder:text-muted-foreground h-full min-w-0 flex-1 bg-transparent text-xs outline-none"
                onKeyDown={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
            />
            {value.length > 0 ? (
                <button
                    type="button"
                    aria-label="Clear search"
                    className="text-muted-foreground hover:text-foreground flex size-4 shrink-0 items-center justify-center rounded-sm outline-none"
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => onChange('')}
                >
                    <X className="size-3.5" />
                </button>
            ) : null}
        </div>
    );
}

function selectedValuesForDimensionChip<K extends string>(
    chips: Array<FilterChip<K>>,
    dimension: FilterDimension<K>,
): Array<string> {
    return chips
        .filter((candidate) => candidate.dimension === dimension.key)
        .flatMap((candidate) => candidate.values);
}

function FilterOptionSelector({
    applied,
    compact,
}: {
    readonly applied: boolean;
    readonly compact: boolean;
}) {
    const boxClass = compact
        ? 'size-3.5 rounded-[3px]'
        : 'size-4 rounded-[4px]';

    if (compact) {
        return (
            <span
                className={cn(
                    'flex shrink-0 items-center justify-center',
                    boxClass,
                )}
                aria-hidden={true}
            >
                <span
                    className={cn(
                        'border-border flex items-center justify-center border shadow-xs',
                        'invisible group-data-[selected=true]/filter-option:visible',
                        boxClass,
                        applied &&
                            'bg-primary border-primary text-primary-foreground',
                    )}
                >
                    {applied ? <Check className="size-2.5 text-white" /> : null}
                </span>
            </span>
        );
    }

    return (
        <span
            className={cn(
                'flex shrink-0 items-center justify-center',
                boxClass,
            )}
            aria-hidden={!applied}
        >
            {applied ? <Check className="size-4" /> : null}
        </span>
    );
}

function FilterOptionList<K extends string>({
    dimension,
    selectedValues,
    onToggle,
    compact = false,
    autoFocusSearch = false,
}: {
    readonly dimension: FilterDimension<K>;
    readonly selectedValues: Array<string>;
    readonly onToggle: (value: string) => void;
    readonly compact?: boolean;
    /** Focus the flyout search on open (opt-in via flyoutKeyboardNavigation:
     * some consumers' flows depend on focus staying in the root input). */
    readonly autoFocusSearch?: boolean;
}) {
    if (dimension.renderValuePicker) {
        return dimension.renderValuePicker({
            selectedValues,
            onSelect: onToggle,
        });
    }

    return (
        <Command className={cn(compact && COMPACT_COMMAND_CLASS)}>
            {compact || dimension.searchable ? (
                <CommandInput
                    autoFocus={autoFocusSearch}
                    placeholder={`Search ${dimension.label.toLowerCase()}…`}
                />
            ) : null}
            <CommandList>
                <CommandEmpty>No options found.</CommandEmpty>
                <CommandGroup>
                    {dimension.options.map((option) => {
                        const isSelected = selectedValues.includes(
                            option.value,
                        );
                        return (
                            <CommandItem
                                key={option.value}
                                value={`${option.label} ${option.value}`}
                                className={cn(compact && 'group/filter-option')}
                                onSelect={() => onToggle(option.value)}
                            >
                                <FilterOptionSelector
                                    applied={isSelected}
                                    compact={compact}
                                />
                                <span className="min-w-0 flex-1 truncate">
                                    {option.label}
                                </span>
                                {option.count !== undefined ? (
                                    <Badge
                                        variant="secondary"
                                        className={cn(
                                            'ml-auto shrink-0',
                                            compact && 'h-5 px-1.5 text-xs',
                                        )}
                                    >
                                        {option.count}
                                    </Badge>
                                ) : null}
                            </CommandItem>
                        );
                    })}
                </CommandGroup>
            </CommandList>
        </Command>
    );
}

function FilterChipPill<K extends string>({
    dimension,
    chip,
    onToggleValue,
    onOperatorChange,
    onRemove,
}: {
    readonly dimension: FilterDimension<K>;
    readonly chip: FilterChip<K>;
    readonly onToggleValue: (value: string) => void;
    readonly onOperatorChange: (operator: FilterOperator) => void;
    readonly onRemove: () => void;
}) {
    const [open, setOpen] = useState(false);
    const Icon = dimension.icon;
    const [firstValue] = chip.values;
    const operator = chipOperator(chip);
    const operators = dimensionOperators(dimension);
    const staticOperatorLabel =
        dimension.operatorLabel ?? operatorLabel(operator, chip.values.length);
    const valueSummary =
        chip.values.length === 1
            ? (dimension.options.find((option) => option.value === firstValue)
                  ?.label ?? firstValue)
            : `${chip.values.length} selected`;

    return (
        <div className="bg-background flex h-8 items-center overflow-hidden rounded-md border text-xs">
            <span className="text-muted-foreground flex items-center gap-1.5 pr-1 pl-2.5">
                <Icon className="h-3.5 w-3.5" />
                {dimension.label}
            </span>
            {operators.length > 1 ? (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild={true}>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground h-8 rounded-none px-1 text-xs font-normal"
                        >
                            {operatorLabel(operator, chip.values.length)}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        {operators.map((candidate) => (
                            <DropdownMenuItem
                                key={candidate}
                                onSelect={() => onOperatorChange(candidate)}
                            >
                                <Check
                                    className={cn(
                                        'mr-2 h-4 w-4',
                                        operator === candidate
                                            ? 'opacity-100'
                                            : 'opacity-0',
                                    )}
                                />
                                {operatorLabel(candidate, chip.values.length)}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : (
                <span className="text-muted-foreground px-1">
                    {staticOperatorLabel}
                </span>
            )}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild={true}>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 max-w-[200px] rounded-none px-1.5 text-xs font-medium"
                    >
                        <span className="truncate">{valueSummary}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="border-border bg-popover w-72 p-0 shadow-lg"
                >
                    <FilterOptionList
                        dimension={dimension}
                        selectedValues={chip.values}
                        onToggle={(value) => {
                            onToggleValue(value);
                            if (!dimension.multiSelect) {
                                setOpen(false);
                            }
                        }}
                    />
                </PopoverContent>
            </Popover>
            <Button
                variant="ghost"
                size="sm"
                className="h-8 rounded-none px-1.5"
                aria-label={`Remove ${dimension.label} filter`}
                onClick={onRemove}
            >
                <X className="h-3 w-3" />
            </Button>
        </div>
    );
}

function AddFilterMenu<K extends string>({
    dimensions,
    chips,
    onToggleValue,
    triggerVariant = 'default',
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    flyoutKeyboardNavigation = false,
}: {
    readonly dimensions: Array<FilterDimension<K>>;
    readonly chips: Array<FilterChip<K>>;
    readonly onToggleValue: (
        dimension: FilterDimension<K>,
        value: string,
    ) => void;
    readonly triggerVariant?: 'compact' | 'default' | 'toolbar';
    readonly open?: boolean;
    readonly onOpenChange?: (open: boolean) => void;
    readonly flyoutKeyboardNavigation?: boolean;
}) {
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;
    const setOpen = controlledOnOpenChange ?? setInternalOpen;
    const [searchQuery, setSearchQuery] = useState('');
    const [searchExpandedDimensionKey, setSearchExpandedDimensionKey] =
        useState<K | null>(null);
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const isSearching = normalizedSearchQuery.length > 0;
    const searchExpandedDimension =
        dimensions.find(
            (dimension) => dimension.key === searchExpandedDimensionKey,
        ) ?? null;
    const matchingOptions = isSearching
        ? dimensions
              .flatMap((dimension) =>
                  dimension.options.flatMap((option) => {
                      const labelRank = searchMatchRank(
                          option.label,
                          normalizedSearchQuery,
                      );
                      const valueRank = searchMatchRank(
                          option.value,
                          normalizedSearchQuery,
                      );
                      const rank =
                          labelRank ??
                          (valueRank === null ? null : valueRank + 2);
                      return rank === null ? [] : [{ dimension, option, rank }];
                  }),
              )
              .sort((left, right) => left.rank - right.rank)
        : [];
    const visibleDimensions = isSearching
        ? dimensions
              .flatMap((dimension) => {
                  const rank = searchMatchRank(
                      dimension.label,
                      normalizedSearchQuery,
                  );
                  return rank === null ? [] : [{ dimension, rank }];
              })
              .sort((left, right) => left.rank - right.rank)
              .map(({ dimension }) => dimension)
        : dimensions;

    const resetMenuState = () => {
        setSearchQuery('');
        setSearchExpandedDimensionKey(null);
    };

    const handleSearchQueryChange = (value: string) => {
        setSearchQuery(value);
        setSearchExpandedDimensionKey(null);
    };

    const selectedValuesForDimension = (dimension: FilterDimension<K>) =>
        selectedValuesForDimensionChip(chips, dimension);

    const handleValueToggle = (
        dimension: FilterDimension<K>,
        value: string,
    ) => {
        onToggleValue(dimension, value);
        if (!dimension.multiSelect) {
            setOpen(false);
            resetMenuState();
        }
    };

    return (
        <DropdownMenu
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    resetMenuState();
                }
            }}
        >
            <DropdownMenuTrigger asChild={true}>
                <Button
                    variant={triggerVariant === 'compact' ? 'ghost' : 'outline'}
                    size="sm"
                    className={cn(
                        'h-8 w-8 p-0',
                        triggerVariant !== 'default' &&
                            'rounded-full shadow-none',
                        triggerVariant === 'compact' && 'text-muted-foreground',
                    )}
                    aria-label={
                        triggerVariant === 'compact'
                            ? 'Add another filter'
                            : 'Add filter'
                    }
                >
                    {triggerVariant === 'compact' ? (
                        <Plus className="h-4 w-4" />
                    ) : (
                        <ListFilter className="h-4 w-4" />
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 p-0 shadow-md">
                {searchExpandedDimension ? (
                    <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground flex h-7 w-full items-center gap-1 border-b px-2 text-xs"
                        onClick={() => setSearchExpandedDimensionKey(null)}
                    >
                        <ChevronLeft className="size-3.5 shrink-0" />
                        {searchExpandedDimension.label}
                    </button>
                ) : (
                    <FilterMenuSearchInput
                        value={searchQuery}
                        onChange={handleSearchQueryChange}
                    />
                )}
                {searchExpandedDimension ? (
                    <FilterOptionList
                        compact={true}
                        dimension={searchExpandedDimension}
                        selectedValues={selectedValuesForDimension(
                            searchExpandedDimension,
                        )}
                        onToggle={(value) =>
                            handleValueToggle(searchExpandedDimension, value)
                        }
                    />
                ) : isSearching ? (
                    <Command
                        shouldFilter={false}
                        className={cn('rounded-none', COMPACT_COMMAND_CLASS)}
                    >
                        <CommandList>
                            <CommandEmpty>No filters found.</CommandEmpty>
                            <CommandGroup>
                                {matchingOptions.map(
                                    ({ dimension, option }) => {
                                        const Icon = dimension.icon;
                                        return (
                                            <CommandItem
                                                key={`${dimension.key}:${option.value}`}
                                                className="h-7"
                                                value={`option:${dimension.key}:${option.value}`}
                                                onSelect={() =>
                                                    handleValueToggle(
                                                        dimension,
                                                        option.value,
                                                    )
                                                }
                                            >
                                                <Icon className="text-muted-foreground size-3.5 shrink-0" />
                                                <span className="text-muted-foreground min-w-16 text-xs">
                                                    {dimension.label}
                                                </span>
                                                <span className="min-w-0 flex-1 truncate">
                                                    {option.label}
                                                </span>
                                            </CommandItem>
                                        );
                                    },
                                )}
                                {visibleDimensions.map((dimension) => {
                                    const Icon = dimension.icon;
                                    return (
                                        <CommandItem
                                            key={dimension.key}
                                            className="h-7"
                                            value={`dimension:${dimension.key}`}
                                            onSelect={() => {
                                                setSearchExpandedDimensionKey(
                                                    dimension.key,
                                                );
                                            }}
                                        >
                                            <Icon className="text-muted-foreground size-3.5 shrink-0" />
                                            {dimension.label}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                ) : (
                    <div className="p-1">
                        {dimensions.map((dimension) => {
                            const Icon = dimension.icon;
                            const hasSelectedValues =
                                selectedValuesForDimension(dimension).length >
                                0;
                            return (
                                <DropdownMenuSub key={dimension.key}>
                                    <DropdownMenuSubTrigger
                                        className={cn(
                                            'h-7 py-1 text-xs',
                                            hasSelectedValues && 'font-medium',
                                        )}
                                    >
                                        <Icon className="text-muted-foreground size-3.5 shrink-0" />
                                        <span>{dimension.label}</span>
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent
                                        className="w-64 p-0"
                                        // The submenu hosts a cmdk Command;
                                        // Radix's menu roving-focus would
                                        // otherwise swallow navigation keys
                                        // before cmdk sees them. Opt-in:
                                        // some consumers' flows depend on
                                        // the default focus behavior.
                                        onKeyDown={(event) => {
                                            if (
                                                flyoutKeyboardNavigation &&
                                                [
                                                    'ArrowUp',
                                                    'ArrowDown',
                                                    'Home',
                                                    'End',
                                                    'Enter',
                                                ].includes(event.key)
                                            ) {
                                                event.stopPropagation();
                                            }
                                        }}
                                    >
                                        <FilterOptionList
                                            compact={true}
                                            autoFocusSearch={
                                                flyoutKeyboardNavigation
                                            }
                                            dimension={dimension}
                                            selectedValues={selectedValuesForDimension(
                                                dimension,
                                            )}
                                            onToggle={(value) =>
                                                handleValueToggle(
                                                    dimension,
                                                    value,
                                                )
                                            }
                                        />
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                            );
                        })}
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export function FilterBar<K extends string>({
    dimensions,
    chips,
    onChange,
    onClear,
    layout = 'inline',
    activeRowActions,
    showClear,
    addMenuOpen,
    onAddMenuOpenChange,
    flyoutKeyboardNavigation,
}: {
    readonly dimensions: Array<FilterDimension<K>>;
    readonly chips: Array<FilterChip<K>>;
    readonly onChange: (chips: Array<FilterChip<K>>) => void;
    readonly onClear?: () => void;
    readonly layout?: 'active-row' | 'inline' | 'trigger';
    readonly activeRowActions?: ReactNode;
    readonly showClear?: boolean;
    readonly addMenuOpen?: boolean;
    readonly onAddMenuOpenChange?: (open: boolean) => void;
    /** Opt-in: autofocus flyout search and keep arrows/Enter with cmdk. */
    readonly flyoutKeyboardNavigation?: boolean;
}) {
    const shouldShowClear = showClear ?? true;
    const handleToggleValue = (
        dimension: FilterDimension<K>,
        value: string,
        operator: FilterOperator = DEFAULT_FILTER_OPERATOR,
    ) => {
        onChange(toggleChipValue(chips, dimension, value, operator));
    };

    const chipPills = chips.map((chip) => {
        const dimension = dimensions.find(
            (candidate) => candidate.key === chip.dimension,
        );
        if (!dimension) {
            return null;
        }
        return (
            <FilterChipPill
                key={`${chip.dimension}:${chipOperator(chip)}`}
                dimension={dimension}
                chip={chip}
                onToggleValue={(value) =>
                    handleToggleValue(dimension, value, chipOperator(chip))
                }
                onOperatorChange={(operator) =>
                    onChange(setChipOperator(chips, chip, dimension, operator))
                }
                onRemove={() =>
                    onChange(chips.filter((candidate) => candidate !== chip))
                }
            />
        );
    });
    const clearFilters = () => {
        if (onClear) {
            onClear();
            return;
        }
        onChange([]);
    };

    if (layout === 'trigger') {
        return (
            <AddFilterMenu
                flyoutKeyboardNavigation={flyoutKeyboardNavigation}
                dimensions={dimensions}
                chips={chips}
                onToggleValue={handleToggleValue}
                triggerVariant="toolbar"
                open={addMenuOpen}
                onOpenChange={onAddMenuOpenChange}
            />
        );
    }

    if (layout === 'active-row') {
        if (chips.length === 0 && showClear !== true) {
            return null;
        }
        return (
            <div
                className="flex w-full min-w-0 items-start gap-2 pt-2"
                role="group"
                aria-label="Active filters"
            >
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    {chipPills}
                    <AddFilterMenu
                        flyoutKeyboardNavigation={flyoutKeyboardNavigation}
                        dimensions={dimensions}
                        chips={chips}
                        onToggleValue={handleToggleValue}
                        triggerVariant="compact"
                    />
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-1">
                    {shouldShowClear ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground h-8 px-2 text-xs"
                            onClick={clearFilters}
                        >
                            Clear
                        </Button>
                    ) : null}
                    {activeRowActions}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-2">
            {chipPills}
            <AddFilterMenu
                flyoutKeyboardNavigation={flyoutKeyboardNavigation}
                dimensions={dimensions}
                chips={chips}
                onToggleValue={handleToggleValue}
            />
            {chips.length > 0 && shouldShowClear ? (
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground h-8 text-xs"
                    onClick={clearFilters}
                >
                    Clear filters
                </Button>
            ) : null}
        </div>
    );
}
